import InfoCallout from "@components/common/info/InfoCallout";
import { KeyStrokeTextDecorator } from "@components/common/info/TextDecorators";
import { faTurnDownLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDarkMode } from "@hooks";
import { TextField, Chip, Autocomplete } from "@mui/material";
import { useState, useEffect, useMemo } from "react";

import { alert } from "../../common/Alert";

export const isValidEmail = (email, array, team) => {
  if (array?.includes(email)) {
    alert("warning", `${email} is already on this team.`);
    return false;
  }
  const invitedMembers = team?.teamInvites
    ?.filter((invite) => invite.status === "pending")
    ?.map((invite) => invite.inviteeEmail);
  if (invitedMembers?.includes(email)) {
    alert("warning", `${email} is already invited to this team.`);
    return false;
  }
  const domain = email?.split("@")[1];
  if (
    team?.emailDomain &&
    domain?.toLowerCase() !== team?.emailDomain.toLowerCase()
  ) {
    alert(
      "error",
      `Your organization only allows users to be added with a specific email domain. The user's email must have an @${team.emailDomain} domain.`,
    );
    return false;
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    alert("error", `${email} is not a valid email.`);
    return false;
  }

  return true;
};

const AddUsers = ({
  newUserEmails,
  setNewUserEmails,
  team = { uuid: "", users: [] },
  isNewTeam,
}) => {
  const [users, setUsers] = useState(newUserEmails || []);
  const [inputValue, setInputValue] = useState("");
  const [enabled] = useDarkMode();

  useEffect(() => {
    setUsers(newUserEmails);
  }, [newUserEmails]);

  const textFieldStyles = useMemo(
    () => ({
      width: "100%",
      resize: "none",
      appearance: "none",
      border: "1px solid",
      borderColor: enabled ? "#374151" : "#f3f4f6",
      backgroundColor: enabled ? "#374151" : "#f9fafb",
      color: enabled ? "white" : "#1f2937",
      borderRadius: "10px",
      "& .MuiInputBase-input": {
        color: enabled ? "white" : "#1f2937",
        fontSize: "14px",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: enabled ? "#1f2937" : "white",
      },
      "& .MuiOutlinedInput-root": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "transparent",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "transparent",
        },
        "&.Mui-focused": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#a5b4fc",
            borderWidth: "2px",
            borderRadius: "10px",
          },
        },
      },
      "& .MuiAutocomplete-clearIndicator": {
        color: enabled ? "white" : "#1f2937",
      },
      "&::placeholder": {
        color: "#9ca3af",
      },
      "&:focus": {
        outline: "none",
        zIndex: 10,
      },
    }),
    [enabled],
  );

  const chipStyles = useMemo(
    () => ({
      margin: "1px",
      borderColor: enabled ? "#374151" : "#f3f4f6",
      backgroundColor: enabled ? "#4b5563" : "#f3f4f6",
      color: enabled ? "white" : "#1f2937",
      "&:hover": {
        borderColor: "#6366f1",
      },
      "& .MuiChip-deleteIcon": {
        color: "#9ca3af",
        "&:hover": {
          color: "#6366f1",
        },
      },
    }),
    [enabled],
  );

  const handleAddUser = (_, newValue) => {
    const teamUsersArray =
      team?.users?.map((user) => user.email) || [];
    const validEmails = newValue?.filter((email) =>
      isValidEmail(email, teamUsersArray, team, isNewTeam),
    );
    setUsers(validEmails);
    setNewUserEmails(validEmails);
  };

  const handleBlur = () => {
    if (users.includes(inputValue.trim())) {
      return false;
    }
    if (inputValue.trim()) {
      const teamUsersArray =
        team?.users?.map((user) => user.email) || [];
      if (isValidEmail(inputValue, teamUsersArray, team)) {
        const updatedUsers = [...users, inputValue];
        setUsers(updatedUsers);
        setNewUserEmails(updatedUsers);
        setInputValue("");
      }
    }
  };

  return (
    <>
      <Autocomplete
        size="small"
        multiple
        freeSolo
        options={[]}
        value={users}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" &&
            users.includes(event.target.value)
          ) {
            alert("error", "You have already included this user.");
          }
        }}
        onChange={handleAddUser}
        onBlur={handleBlur}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={option}
              {...getTagProps({ index })}
              key={index}
              sx={chipStyles}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="Add user"
            sx={textFieldStyles}
            data-testid="add-user-textarea"
          />
        )}
      />
      <InfoCallout>
        Type in the <strong>email</strong> of each new team member,
        then{" "}
        <KeyStrokeTextDecorator>
          <FontAwesomeIcon icon={faTurnDownLeft} /> Enter
        </KeyStrokeTextDecorator>{" "}
        to add it to the list.
      </InfoCallout>
    </>
  );
};

export default AddUsers;
